import { useCallback, useEffect } from 'react';
import {
  intlNumberFormatter,
  IntlNumberFormatterOptions,
  useFormattedInput,
} from 'react-formatted-input-hook';

import { useTranslation } from '@mint-lib/i18n';
import { useProfile } from '@mint-lib/profile-helpers';

import TextField, { TextFieldProps } from '../TextField/TextField.jsx';

export interface NumberFieldProps extends Omit<
  TextFieldProps,
  'value' | 'onChange'
> {
  value?: number | null;
  onChange?: (value: number | null, formattedValue: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  maxDecimals?: number;
  restFormatProps?: Omit<
    IntlNumberFormatterOptions,
    'location' | 'value' | 'onChange'
  >;
}

/**
 * A component that renders a text field for numbers with formatting options.
 * @param value - The value of the number field.
 * @param onChange - The function to call when the value of the number field changes.
 * @param maxDecimals - The maximum number of decimal places to display.
 * @param restFormatProps - Additional formatting options for the number field.
 * @param autoFocus - If `true`, the number field will be focused on mount.
 * @param onFocus - The function to call when the number field gains focus.
 * @param onBlur - The function to call when the number field loses focus.
 * @param restProps - Additional props to pass to the underlying `TextField` component.
 * @returns A `TextField` component with number formatting options.
 */
const NumberField = ({
  value,
  onChange,
  onFocus,
  onBlur,
  maxDecimals = 2,
  restFormatProps = {},
  autoFocus,
  InputProps: restInputProps,
  ...restProps
}: NumberFieldProps) => {
  const { i18n } = useTranslation('@myn/mui');

  const { getProfileField } = useProfile();

  // remove after the full release;
  const locale = (
    getProfileField('formattingConfig')?.numbersFormat
      ? getProfileField('formattingConfig')?.numbersFormat
      : i18n.language
  ) as string;

  const formattedInput = useFormattedInput(
    intlNumberFormatter({
      ...restFormatProps,
      locales: locale,
      value,
      onChange,
      maxDecimals,
      liveUpdate: true,
    }),
  );

  const {
    ref,
    onFocus: libOnFocus,
    onBlur: libOnBlur,
    ...rest
  } = formattedInput.props;

  // A workaround for the issue with the `autoFocus` prop. You can read more about it here: https://github.com/nick-keller/react-formatted-input-hook/issues/3
  useEffect(() => {
    if (formattedInput.props.ref.current && autoFocus === true) {
      formattedInput.props.ref.current.focus();
    }
  }, [formattedInput.props.ref.current == null, autoFocus]);

  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(event);
      }
      libOnFocus(event);
    },
    [onFocus, libOnFocus],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(event);
      }
      libOnBlur(event);
    },
    [onBlur, libOnBlur],
  );

  return (
    <TextField
      {...restProps}
      InputProps={{
        inputRef: ref,
        onFocus: handleFocus,
        onBlur: handleBlur,
        ...rest,
        ...restInputProps,
      }}
    />
  );
};

export default NumberField;
